/**
 * 用户退出登录清理服务
 * 处理用户主动退出登录时的资源清理工作
 */

import { audioManager } from './audio';
import { mqttService } from './mqttService';
import { authService } from './authService';
import { realtimeService } from './realtimeService';

export interface LogoutCleanupOptions {
  force?: boolean; // 是否强制清理，忽略错误
  silent?: boolean; // 是否静默执行，不输出日志
}

class LogoutCleanupService {
  private static instance: LogoutCleanupService;
  private isCleaning = false;

  private constructor() {}

  public static getInstance(): LogoutCleanupService {
    if (!LogoutCleanupService.instance) {
      LogoutCleanupService.instance = new LogoutCleanupService();
    }
    return LogoutCleanupService.instance;
  }

  /**
   * 执行完整的退出登录清理
   */
  public async performLogoutCleanup(options: LogoutCleanupOptions = {}): Promise<void> {
    if (this.isCleaning) {
      if (!options.silent) {
        console.log('清理工作正在进行中，跳过重复执行');
      }
      return;
    }

    this.isCleaning = true;

    try {
      if (!options.silent) {
        console.log('=== 开始用户退出登录清理 ===');
      }

      // 1. 清理音频资源
      await this.cleanupAudioResources(options);

      // 2. 清理MQTT连接和订阅
      await this.cleanupMqttResources(options);

      // 3. 清理频谱显示资源
      await this.cleanupSpectrumResources(options);

      // 4. 清理其他UI资源
      await this.cleanupUIResources(options);

      // 5. 清理缓存和存储
      await this.cleanupStorage(options);

      if (!options.silent) {
        console.log('=== 用户退出登录清理完成 ===');
      }

    } catch (error) {
      if (!options.silent) {
        console.error('用户退出登录清理失败:', error);
      }
      if (!options.force) {
        throw error;
      }
    } finally {
      this.isCleaning = false;
    }
  }

  /**
   * 清理音频资源
   */
  private async cleanupAudioResources(options: LogoutCleanupOptions): Promise<void> {
    try {
      if (!options.silent) {
        console.log('清理音频资源...');
      }

      // 停止所有音频录制
      const audioState = audioManager.getState();
      if (audioState.isRecording) {
        audioManager.stopRecording();
        if (!options.silent) {
          console.log('音频录制已停止');
        }
      }

      // 注意：退出登录时不调用 stopListeningDevice()，因为这会删除后端的连接记录
      // 退出登录只清理前端状态，连接记录应该由用户主动"断开设备"操作来删除
      // 这样用户重新登录后，仍然可以看到之前的设备
      // 只清理前端音频状态，不调用后端API
      if (!options.silent) {
        console.log('跳过停止设备监听（保留后端连接记录）');
      }

      // 清理音频缓冲区
      this.clearAudioBuffers();

      // 销毁音频管理器
      await audioManager.dispose();
      if (!options.silent) {
        console.log('音频管理器已销毁');
      }

    } catch (error) {
      if (!options.silent) {
        console.error('清理音频资源失败:', error);
      }
      if (!options.force) {
        throw error;
      }
    }
  }

  /**
   * 清理MQTT资源
   */
  private async cleanupMqttResources(options: LogoutCleanupOptions): Promise<void> {
    try {
      if (!options.silent) {
        console.log('清理MQTT资源...');
      }

      realtimeService.resetMqttClientSession();

      // 检查MQTT服务是否已初始化
      if (!mqttService.isServiceInitialized()) {
        if (!options.silent) {
          console.log('MQTT服务未初始化，跳过清理');
        }
        return;
      }

      // 取消所有MQTT订阅
      try {
      const mqttManager = mqttService.getMqttManager();
      const subscriptions = mqttManager.getSubscriptions();
      
      for (const topic of subscriptions) {
        try {
          await mqttManager.unsubscribe(topic);
          if (!options.silent) {
            console.log(`MQTT订阅已取消: ${topic}`);
          }
        } catch (error) {
          if (!options.silent) {
            console.warn(`取消MQTT订阅失败 ${topic}:`, error);
          }
          }
        }
      } catch (error) {
        if (!options.silent) {
          console.warn('获取MQTT订阅列表失败:', error);
        }
      }

      // 彻底销毁MQTT服务（断开连接并重置状态）
        try {
        await mqttService.dispose();
          if (!options.silent) {
          console.log('MQTT服务已销毁');
          }
        } catch (error) {
          if (!options.silent) {
          console.warn('销毁MQTT服务失败:', error);
        }
      }

    } catch (error) {
      if (!options.silent) {
        console.error('清理MQTT资源失败:', error);
      }
      if (!options.force) {
        throw error;
      }
    }
  }

  /**
   * 清理频谱显示资源
   */
  private async cleanupSpectrumResources(options: LogoutCleanupOptions): Promise<void> {
    try {
      if (!options.silent) {
        console.log('清理频谱显示资源...');
      }

      // 清理频谱调试工具
      if (window.debugSpectrum) {
        try {
          window.debugSpectrum.reset();
          if (!options.silent) {
            console.log('频谱调试工具已重置');
          }
        } catch (error) {
          if (!options.silent) {
            console.warn('重置频谱调试工具失败:', error);
          }
        }
      }

      // 清理频谱性能优化器
      if (window.performanceOptimizer) {
        try {
          window.performanceOptimizer.reset();
          if (!options.silent) {
            console.log('频谱性能优化器已重置');
          }
        } catch (error) {
          if (!options.silent) {
            console.warn('重置频谱性能优化器失败:', error);
          }
        }
      }

    } catch (error) {
      if (!options.silent) {
        console.error('清理频谱显示资源失败:', error);
      }
      if (!options.force) {
        throw error;
      }
    }
  }

  /**
   * 清理UI资源
   */
  private async cleanupUIResources(options: LogoutCleanupOptions): Promise<void> {
    try {
      if (!options.silent) {
        console.log('清理UI资源...');
      }

      // 清理所有定时器
      this.clearAllTimers();

      // 清理所有事件监听器
      this.clearAllEventListeners();

      // 清理WebSocket连接
      this.clearWebSocketConnections();

      if (!options.silent) {
        console.log('UI资源已清理');
      }

    } catch (error) {
      if (!options.silent) {
        console.error('清理UI资源失败:', error);
      }
      if (!options.force) {
        throw error;
      }
    }
  }

  /**
   * 清理存储和缓存
   */
  private async cleanupStorage(options: LogoutCleanupOptions): Promise<void> {
    try {
      if (!options.silent) {
        console.log('清理存储和缓存...');
      }

      // 在清理 sessionStorage 前保存登出标志，以防止被清空
      let logoutFlag = null;
      try {
        logoutFlag = sessionStorage.getItem('wavelink_logged_out');
      } catch (error) {
        // ignore
      }

      // 清理localStorage中的用户相关数据
      this.clearUserLocalStorage();

      // 清理sessionStorage
      this.clearSessionStorage();

      // 恢复登出标志，避免清理过程中丢失
      if (logoutFlag === 'true') {
        try {
          sessionStorage.setItem('wavelink_logged_out', 'true');
          if (!options.silent) {
            console.log('已恢复登出标志');
          }
        } catch (error) {
          console.warn('恢复登出标志失败:', error);
        }
      }

      // 清理IndexedDB（如果有）
      this.clearIndexedDB();

      // 强制垃圾回收（如果可用）
      if (window.gc) {
        window.gc();
        if (!options.silent) {
          console.log('强制垃圾回收已执行');
        }
      }

      if (!options.silent) {
        console.log('存储和缓存已清理');
      }

    } catch (error) {
      if (!options.silent) {
        console.error('清理存储和缓存失败:', error);
      }
      if (!options.force) {
        throw error;
      }
    }
  }

  /**
   * 清理音频缓冲区
   * 注意：AudioManager.dispose() 已经会清理所有缓冲区，
   * 这里主要是为了在 dispose() 之前进行额外的清理
   */
  private clearAudioBuffers(): void {
    try {
      // AudioManager.dispose() 会清理所有缓冲区
      // 这里不需要额外操作，因为 dispose() 已经处理了
      // 保留此方法以防将来需要额外的清理逻辑
      console.log('音频缓冲区将在 AudioManager.dispose() 中清理');
    } catch (error) {
      console.warn('清理音频缓冲区失败:', error);
    }
  }

  /**
   * 清理所有定时器
   */
  private clearAllTimers(): void {
    try {
      // 清理所有setTimeout和setInterval
      // 注意：这只能清理我们知道ID的定时器
      // 实际应用中应该在创建定时器时记录ID
      
      // 清理频谱相关的定时器
      if (window.debugSpectrum) {
        // 重置会清理内部的定时器
        window.debugSpectrum.reset();
      }

      // 发送清理定时器事件
      window.dispatchEvent(new CustomEvent('clear-all-timers'));
      
    } catch (error) {
      console.warn('清理定时器失败:', error);
    }
  }

  /**
   * 清理所有事件监听器
   */
  private clearAllEventListeners(): void {
    try {
      // 发送清理事件监听器事件
      window.dispatchEvent(new CustomEvent('clear-all-event-listeners'));
      
    } catch (error) {
      console.warn('清理事件监听器失败:', error);
    }
  }

  /**
   * 清理WebSocket连接
   */
  private clearWebSocketConnections(): void {
    try {
      // 发送清理WebSocket事件
      window.dispatchEvent(new CustomEvent('clear-websocket-connections'));
      
    } catch (error) {
      console.warn('清理WebSocket连接失败:', error);
    }
  }

  /**
   * 清理用户相关的localStorage
   */
  private clearUserLocalStorage(): void {
    try {
      const user = authService.getUser();
      if (user) {
        const userId = user.id.toString();
        
        // 清理用户相关的localStorage项
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes(userId) || key.includes('user_') || key.includes('auth_'))) {
            keysToRemove.push(key);
          }
        }
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });
        
        console.log(`已清理 ${keysToRemove.length} 个用户相关的localStorage项`);
      }
    } catch (error) {
      console.warn('清理localStorage失败:', error);
    }
  }

  /**
   * 清理sessionStorage
   */
  private clearSessionStorage(): void {
    try {
      sessionStorage.clear();
      console.log('sessionStorage已清理');
    } catch (error) {
      console.warn('清理sessionStorage失败:', error);
    }
  }

  /**
   * 清理IndexedDB
   */
  private clearIndexedDB(): void {
    try {
      // 如果有使用IndexedDB，在这里清理
      // 目前项目中没有使用IndexedDB，所以暂时空实现
      console.log('IndexedDB清理跳过（未使用）');
    } catch (error) {
      console.warn('清理IndexedDB失败:', error);
    }
  }

  /**
   * 快速清理（用于紧急情况）
   */
  public async quickCleanup(): Promise<void> {
    await this.performLogoutCleanup({ 
      force: true, 
      silent: true 
    });
  }

  /**
   * 检查是否正在清理
   */
  public isCleaningInProgress(): boolean {
    return this.isCleaning;
  }
}

// 导出单例实例
export const logoutCleanupService = LogoutCleanupService.getInstance();

// 扩展Window接口以支持调试
declare global {
  interface Window {
    logoutCleanupService?: LogoutCleanupService;
    performanceOptimizer?: {
      reset: () => void;
    };
  }
}

// 将服务挂载到window对象以便调试
if (typeof window !== 'undefined') {
  window.logoutCleanupService = logoutCleanupService;
}
